import { logService, LOG_CATEGORIES } from '@/services/logService'
import { DB_VERSION, IndexedDBMigrations } from '@/stores/IndexedDBMigrations'
import type { Event } from '@/types/event'

export const STORES = {
  EVENTS: 'events',
  METADATA: 'metadata',
}

const EVENT_QUEUE_OWNER_KEY = 'eventQueueOwner'

interface EventQueueOwnerRecord {
  key: typeof EVENT_QUEUE_OWNER_KEY
  ownerUserId: number
  locked: boolean
}

interface PersistedEventQueueOwner {
  ownerUserId: number
  locked: boolean
}

export interface EventQueueOwnerContext {
  ownerUserId: number
  generation: number
}

export interface EventQueueActivation {
  context: EventQueueOwnerContext | null
  replacedQueue: boolean
}

type OwnedEventOperation<T> = (
  store: IDBObjectStore,
  succeed: (result: T) => void,
  fail: (error: Error) => void,
) => void

class IndexedDBService {
  private db: IDBDatabase | null = null
  private initPromise: Promise<IDBDatabase> | null = null
  private blockedOpenPending = false
  private eventOwnerUserId: number | null = null
  private eventOwnerGeneration = 0
  private eventOwnerInitialized = false

  async getDB(): Promise<IDBDatabase> {
    if (this.db) return this.db
    if (this.initPromise) return this.initPromise

    let abandoned = false
    const initPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open('routecamp', DB_VERSION)

      request.onerror = () => {
        if (abandoned) {
          this.blockedOpenPending = false
          if (this.initPromise === initPromise) this.initPromise = null
          return
        }
        reject(request.error || new Error('Failed to open database'))
      }
      request.onblocked = () => {
        abandoned = true
        this.blockedOpenPending = true
        reject(new Error('Database upgrade is blocked by another open RouteCamp tab'))
      }
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        const oldVersion = event.oldVersion
        const newVersion = event.newVersion || DB_VERSION

        logService.info(LOG_CATEGORIES.DATABASE, 'Database upgrade needed', {
          oldVersion,
          newVersion,
        })
        if (!db.objectStoreNames.contains(STORES.EVENTS)) {
          const eventsStore = db.createObjectStore(STORES.EVENTS, { keyPath: 'clientId' })
          eventsStore.createIndex('timestamp', 'timestamp', { unique: false })
        }
        if (!db.objectStoreNames.contains(STORES.METADATA)) {
          db.createObjectStore(STORES.METADATA, { keyPath: 'key' })
        }
        IndexedDBMigrations.applyMigrations(db, oldVersion, newVersion)
      }
      request.onsuccess = () => {
        const db = request.result
        if (abandoned) {
          db.close()
          this.blockedOpenPending = false
          if (this.initPromise === initPromise) this.initPromise = null
          return
        }
        db.onversionchange = () => {
          logService.info(LOG_CATEGORIES.DATABASE, 'Closing database for a schema upgrade')
          this.closeDB()
        }
        this.db = db
        resolve(db)
      }
    })

    this.initPromise = initPromise
    try {
      return await initPromise
    } catch (error) {
      if (!abandoned && this.initPromise === initPromise) this.initPromise = null
      throw error
    }
  }

  private validateOwnerUserId(ownerUserId: number): void {
    if (!Number.isSafeInteger(ownerUserId) || ownerUserId <= 0) {
      throw new Error('Invalid event queue owner')
    }
  }

  private parseEventQueueOwner(record: unknown): PersistedEventQueueOwner | null {
    if (!record || typeof record !== 'object') return null
    const candidate = record as Partial<EventQueueOwnerRecord>
    return candidate.key === EVENT_QUEUE_OWNER_KEY &&
      typeof candidate.ownerUserId === 'number' &&
      Number.isSafeInteger(candidate.ownerUserId) &&
      candidate.ownerUserId > 0 &&
      typeof candidate.locked === 'boolean'
      ? { ownerUserId: candidate.ownerUserId, locked: candidate.locked }
      : null
  }

  private resetLocalEventOwner(): number {
    this.eventOwnerGeneration++
    this.eventOwnerUserId = null
    this.eventOwnerInitialized = false
    return this.eventOwnerGeneration
  }

  private assertEventOwner(context: EventQueueOwnerContext): void {
    if (!this.isEventOwnerCurrent(context)) {
      throw new Error('Event queue account changed')
    }
  }

  isEventOwnerCurrent(context: EventQueueOwnerContext): boolean {
    return (
      this.eventOwnerInitialized &&
      this.eventOwnerUserId === context.ownerUserId &&
      this.eventOwnerGeneration === context.generation
    )
  }

  lockEventOwnerLocally(): void {
    this.resetLocalEventOwner()
  }

  private async runOwnedEventTransaction<T>(
    context: EventQueueOwnerContext,
    mode: IDBTransactionMode,
    operation: OwnedEventOperation<T>,
  ): Promise<T> {
    this.assertEventOwner(context)
    const db = await this.getDB()
    this.assertEventOwner(context)

    const result = await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction([STORES.EVENTS, STORES.METADATA], mode)
      const ownerRequest = transaction.objectStore(STORES.METADATA).get(EVENT_QUEUE_OWNER_KEY)
      let operationError: Error | null = null
      let operationResult: T
      let operationCompleted = false

      const fail = (error: Error) => {
        operationError = error
        try {
          transaction.abort()
        } catch (abortError) {
          reject(
            abortError instanceof Error
              ? new Error(`${error.message}: ${abortError.message}`)
              : error,
          )
        }
      }

      ownerRequest.onsuccess = () => {
        const owner = this.parseEventQueueOwner(ownerRequest.result)
        if (owner?.ownerUserId !== context.ownerUserId || owner.locked) {
          fail(new Error('Event queue account changed'))
          return
        }
        try {
          operation(
            transaction.objectStore(STORES.EVENTS),
            (value) => {
              operationResult = value
              operationCompleted = true
            },
            fail,
          )
        } catch (error) {
          fail(error instanceof Error ? error : new Error(String(error)))
        }
      }
      ownerRequest.onerror = () => {
        operationError = ownerRequest.error || new Error('Failed to verify event queue owner')
      }
      transaction.oncomplete = () => {
        if (!operationCompleted) {
          reject(operationError || new Error('Event queue operation did not complete'))
          return
        }
        resolve(operationResult)
      }
      transaction.onerror = () =>
        reject(operationError || transaction.error || new Error('Event queue transaction failed'))
      transaction.onabort = () =>
        reject(
          operationError || transaction.error || new Error('Event queue transaction was aborted'),
        )
    })

    this.assertEventOwner(context)
    return result
  }

  async activateEventOwner(ownerUserId: number): Promise<EventQueueActivation> {
    this.validateOwnerUserId(ownerUserId)

    const generation = this.resetLocalEventOwner()
    const db = await this.getDB()
    const replacedQueue = await new Promise<boolean>((resolve, reject) => {
      const transaction = db.transaction([STORES.EVENTS, STORES.METADATA], 'readwrite')
      const eventsStore = transaction.objectStore(STORES.EVENTS)
      const metadataStore = transaction.objectStore(STORES.METADATA)
      const ownerRequest = metadataStore.get(EVENT_QUEUE_OWNER_KEY)
      let replaced = false

      ownerRequest.onsuccess = () => {
        const existingOwner = this.parseEventQueueOwner(ownerRequest.result)
        if (existingOwner?.ownerUserId !== ownerUserId) {
          replaced = true
          eventsStore.clear()
          metadataStore.put({ key: EVENT_QUEUE_OWNER_KEY, ownerUserId, locked: false })
        } else if (existingOwner.locked) {
          metadataStore.put({ key: EVENT_QUEUE_OWNER_KEY, ownerUserId, locked: false })
        }
      }
      ownerRequest.onerror = () => reject(ownerRequest.error || new Error('Failed to read owner'))
      transaction.oncomplete = () => resolve(replaced)
      transaction.onerror = () =>
        reject(transaction.error || new Error('Failed to activate event queue'))
      transaction.onabort = () =>
        reject(transaction.error || new Error('Event queue activation was aborted'))
    })

    if (this.eventOwnerGeneration !== generation) {
      throw new Error('Event queue account changed during activation')
    }
    this.eventOwnerUserId = ownerUserId
    this.eventOwnerInitialized = true
    return { context: { ownerUserId, generation }, replacedQueue }
  }

  async lockEventOwner(expectedOwnerUserId?: number): Promise<EventQueueActivation> {
    if (expectedOwnerUserId !== undefined) this.validateOwnerUserId(expectedOwnerUserId)

    const generation = this.resetLocalEventOwner()
    const db = await this.getDB()
    const replacedQueue = await new Promise<boolean>((resolve, reject) => {
      const transaction = db.transaction([STORES.EVENTS, STORES.METADATA], 'readwrite')
      const eventsStore = transaction.objectStore(STORES.EVENTS)
      const metadataStore = transaction.objectStore(STORES.METADATA)
      const ownerRequest = metadataStore.get(EVENT_QUEUE_OWNER_KEY)
      let replaced = false

      ownerRequest.onsuccess = () => {
        const existingOwner = this.parseEventQueueOwner(ownerRequest.result)
        if (!existingOwner) {
          replaced = true
          eventsStore.clear()
          metadataStore.delete(EVENT_QUEUE_OWNER_KEY)
        } else if (
          expectedOwnerUserId !== undefined &&
          existingOwner.ownerUserId === expectedOwnerUserId &&
          !existingOwner.locked
        ) {
          metadataStore.put({
            key: EVENT_QUEUE_OWNER_KEY,
            ownerUserId: expectedOwnerUserId,
            locked: true,
          })
        }
      }
      ownerRequest.onerror = () => reject(ownerRequest.error || new Error('Failed to read owner'))
      transaction.oncomplete = () => resolve(replaced)
      transaction.onerror = () =>
        reject(transaction.error || new Error('Failed to lock event queue'))
      transaction.onabort = () =>
        reject(transaction.error || new Error('Event queue lock was aborted'))
    })

    if (this.eventOwnerGeneration !== generation) {
      throw new Error('Event queue account changed during lock')
    }
    this.eventOwnerInitialized = true
    return { context: null, replacedQueue }
  }

  async assertEventOwnerCurrent(context: EventQueueOwnerContext): Promise<void> {
    return this.runOwnedEventTransaction(context, 'readonly', (_store, succeed) => {
      succeed(undefined)
    })
  }

  async getEventsSince(timestamp: number, context: EventQueueOwnerContext): Promise<Event[]> {
    return this.runOwnedEventTransaction(context, 'readonly', (store, succeed, fail) => {
      const request = store.index('timestamp').getAll(IDBKeyRange.lowerBound(timestamp, true))
      request.onsuccess = () => {
        succeed(request.result || [])
      }
      request.onerror = () => fail(request.error || new Error('Failed to get events'))
    })
  }

  async getAllEvents(context: EventQueueOwnerContext): Promise<Event[]> {
    return this.runOwnedEventTransaction(context, 'readonly', (store, succeed, fail) => {
      const request = store.index('timestamp').getAll()
      request.onsuccess = () => {
        succeed(request.result || [])
      }
      request.onerror = () => fail(request.error || new Error('Failed to get events'))
    })
  }

  async saveEvent(event: Event, context: EventQueueOwnerContext): Promise<string> {
    return this.runOwnedEventTransaction(context, 'readwrite', (store, succeed) => {
      store.put(event)
      succeed(event.clientId)
    })
  }

  async saveEvents(events: Event[], context: EventQueueOwnerContext): Promise<void> {
    return this.runOwnedEventTransaction(context, 'readwrite', (store, succeed) => {
      for (const event of events) store.put(event)
      succeed(undefined)
    })
  }

  async deleteEvents(ids: string[], context: EventQueueOwnerContext): Promise<void> {
    if (ids.length === 0) {
      this.assertEventOwner(context)
      return
    }
    return this.runOwnedEventTransaction(context, 'readwrite', (store, succeed) => {
      for (const id of ids) store.delete(id)
      succeed(undefined)
    })
  }

  async clearEvents(context: EventQueueOwnerContext): Promise<void> {
    return this.runOwnedEventTransaction(context, 'readwrite', (store, succeed) => {
      store.clear()
      succeed(undefined)
    })
  }

  async deleteAllDatabases(): Promise<void> {
    this.resetLocalEventOwner()
    this.closeDB()
    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase('routecamp')
      request.onsuccess = () => {
        this.db = null
        this.initPromise = null
        resolve()
      }
      request.onerror = () => reject(request.error || new Error('Failed to delete database'))
      request.onblocked = () => {
        this.db = null
        this.initPromise = null
        resolve()
      }
    })
  }

  async forceRecreateDatabase(): Promise<void> {
    await this.deleteAllDatabases()
  }

  closeDB(): void {
    this.db?.close()
    this.db = null
    if (!this.blockedOpenPending) this.initPromise = null
  }
}

// Create and export a singleton instance
export const dbService = new IndexedDBService()

// Set up cleanup handlers for page unload
if (typeof window !== 'undefined') {
  // Handle page refresh/close
  window.addEventListener('beforeunload', () => {
    dbService.closeDB()
  })

  // Scheduling helpers for visibility-based close
  let closeDbHandle: number | null = null
  let cancelScheduledClose: ((id: number) => void) | null = null

  // Local typings to avoid using `any`
  type IdleDeadline = { didTimeout: boolean; timeRemaining: () => number }
  type RequestIdleCallbackType = (
    cb: (deadline: IdleDeadline) => void,
    opts?: { timeout?: number },
  ) => number
  type CancelIdleCallbackType = (id: number) => void

  const scheduleCloseWhenIdle = () => {
    // Prefer requestIdleCallback if available, fall back to a short timeout
    const rIC = (window as Window).requestIdleCallback as unknown as
      | RequestIdleCallbackType
      | undefined
    const cIC = (window as Window).cancelIdleCallback as unknown as
      | CancelIdleCallbackType
      | undefined
    if (rIC) {
      cancelScheduledClose = cIC || null
      closeDbHandle = rIC(
        () => {
          if (document.visibilityState === 'hidden') {
            dbService.closeDB()
          }
          closeDbHandle = null
        },
        { timeout: 1000 },
      )
    } else {
      cancelScheduledClose = (id: number) => clearTimeout(id)
      closeDbHandle = window.setTimeout(() => {
        if (document.visibilityState === 'hidden') {
          dbService.closeDB()
        }
        closeDbHandle = null
      }, 1000)
    }
  }

  // Handle page visibility changes (mobile browsers)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Defer closing to idle instead of a fixed delay
      if (closeDbHandle == null) scheduleCloseWhenIdle()
    } else {
      // If the page became visible again, cancel any scheduled close
      if (closeDbHandle != null) {
        if (cancelScheduledClose) {
          cancelScheduledClose(closeDbHandle)
        } else {
          clearTimeout(closeDbHandle)
        }
        closeDbHandle = null
      }
    }
  })

  // Handle browser navigation
  window.addEventListener('pagehide', () => {
    dbService.closeDB()
  })
}

// Also export the class for testing purposes
export { IndexedDBService }
